import {
	adobeFileExtensionDef,
	type FileExtension,
	type FileId,
	FileType,
	type ISODate,
	msFileExtensionDef,
	type PeriodType,
	type SuretyAccountId,
	type UploadedFile,
} from "@inrev/common";
import { useAttachFinancialExtraction } from "@portal/api/account";
import { useFileUpload } from "@portal/api/bond-request/agent";
import { FileUploadStatus } from "@portal/api/files";
import { FileUploadStatusIndicator } from "@portal/components/features/file-upload";
import { Modal } from "@portal/components/layout/Modal";
import { ModalItemWithHeader } from "@portal/components/layout/ModalItemWithHeader";
import { Text } from "@portal/components/primitives";
import { Dropzone } from "@portal/components/ui/Dropzone";
import { DateTime } from "luxon";
import type { FC } from "react";
import { useCallback, useState } from "react";
import { v4 } from "uuid";
import type { UploadFile } from "../../../../../types";
import { getNameAndExtensionFromFileName } from "../../../../../utils";

export type FinancialStatementUploadModalProps = {
	accountId: SuretyAccountId;
	periodEndDate: ISODate;
	periodType: PeriodType;
	onClose: () => void;
};

const financialStatementExtensions: FileExtension[] = [
	...adobeFileExtensionDef,
	...msFileExtensionDef,
];

export const FinancialStatementUploadModal: FC<FinancialStatementUploadModalProps> = ({
	accountId,
	periodEndDate,
	onClose,
}) => {
	const { attachFinancialExtractionAsync, attachFinancialExtractionIsLoading } =
		useAttachFinancialExtraction(accountId);
	const [isUploading, setIsUploading] = useState(false);

	const attachAfterUpload = useCallback(
		(uploaded: UploadedFile) => {
			void attachFinancialExtractionAsync({ fileId: uploaded.id }, { onSuccess: onClose }).catch(
				() => {
					setIsUploading(false);
				},
			);
		},
		[attachFinancialExtractionAsync, onClose],
	);

	const { uploadFn } = useFileUpload<FileType>({
		onUploadSuccess: useCallback(
			(data) => {
				const uploaded = data[0];
				if (uploaded) {
					void attachAfterUpload(uploaded);
				} else {
					setIsUploading(false);
				}
			},
			[attachAfterUpload],
		),
		onUploadError: useCallback(() => {
			setIsUploading(false);
		}, []),
		onUpdateSuccess: () => {},
		onUpdateError: () => {},
		onDeleteSuccess: () => {},
		onDeleteError: () => {},
	});

	const handleFiles = useCallback(
		(files: File[]) => {
			const file = files[0];
			if (!file) {
				return;
			}
			const { fileName, extension } = getNameAndExtensionFromFileName(file.name);
			const pendingFile: UploadFile<FileType> = {
				id: v4() as FileId,
				name: fileName,
				extension,
				types: [],
			};
			setIsUploading(true);
			uploadFn({
				fileUploads: [{ rawFile: file, pendingFile }],
			});
		},
		[uploadFn],
	);

	const isBusy = isUploading || attachFinancialExtractionIsLoading;
	const formattedPeriodEndDate = DateTime.fromISO(periodEndDate).toFormat("MM/dd/yyyy");

	return (
		<Modal onClickOutside={isBusy ? undefined : onClose}>
			<ModalItemWithHeader
				header="Upload FYE financial statement"
				onClose={onClose}
				closeDisabled={isBusy}
			>
				<div className="flex min-w-[480px] flex-col gap-4 px-5 py-4">
					<Text type="body" color="primary">
						Upload the financial statement for the fiscal year ending{" "}
						<Text type="body-strong">{formattedPeriodEndDate}</Text>.
					</Text>
					<Dropzone
						allowedExtensions={financialStatementExtensions}
						disabled={isBusy}
						onSuccess={handleFiles}
						placeholderText="Drag and drop or click to select your FYE financial statement"
					/>
					{isBusy && (
						<div className="flex items-center gap-2" aria-live="polite">
							<FileUploadStatusIndicator
								status={FileUploadStatus.uploading}
								id="financial-statement-upload"
							/>
							<Text type="body" color="secondary">
								{attachFinancialExtractionIsLoading
									? "Linking statement and starting extraction…"
									: "Uploading FYE statement…"}
							</Text>
						</div>
					)}
				</div>
			</ModalItemWithHeader>
		</Modal>
	);
};
